import { utmFromSearch } from './attribution';
import type { CheckoutUtmAttribution } from './attribution';
import {
  fetchPurchaseTracking,
  PurchaseTrackingRequestError,
} from './api';
import { trackPurchaseValues, waitForTrackingScripts } from './tracking-pixels';

// Paid and ledger snapshots arrive asynchronously. Keep the handoff alive long
// enough for both, while still bounding summary/external-navigation waits.
export const PURCHASE_TRACKING_BUDGET_MS = 15000;
export type PurchaseTrackingOutcome = 'queued' | 'empty' | 'unavailable' | 'timeout' | 'no_session';

const purchases = new Map<string, {
  readonly originalSaleId: string;
  readonly result: Promise<PurchaseTrackingOutcome>;
}>();

export function trackConfirmedPurchase(
  checkoutId: string,
  originalSaleId: string,
  saleId: string,
  handoffId?: string,
  utm: CheckoutUtmAttribution | undefined = utmFromSearch(new URLSearchParams(window.location.search)),
): Promise<PurchaseTrackingOutcome> {
  if (!handoffId) return Promise.resolve('no_session');
  const key = `${checkoutId}:${saleId}`;
  const existing = purchases.get(key);
  if (existing !== undefined) return existing.result;
  const result = trackWithinBudget(checkoutId, saleId, handoffId, utm);
  purchases.set(key, { originalSaleId, result });
  return result;
}

export async function afterPurchaseTracking(originalSaleId: string, navigate: () => void): Promise<void> {
  await Promise.allSettled(
    [...purchases.values()]
      .filter((purchase) => purchase.originalSaleId === originalSaleId)
      .map((purchase) => purchase.result),
  );
  navigate();
}

async function trackWithinBudget(
  checkoutId: string,
  saleId: string,
  handoffId: string,
  utm?: CheckoutUtmAttribution,
): Promise<PurchaseTrackingOutcome> {
  const controller = new AbortController();
  let attempts = 0;
  let timer: ReturnType<typeof setTimeout>;
  const expired = new Promise<PurchaseTrackingOutcome>((resolve) => {
    timer = setTimeout(() => {
      controller.abort();
      resolve('timeout');
    }, PURCHASE_TRACKING_BUDGET_MS);
  });

  async function run(): Promise<PurchaseTrackingOutcome> {
    while (!controller.signal.aborted) {
      attempts += 1;
      try {
        const tracking = await fetchPurchaseTracking(checkoutId, saleId, handoffId, controller.signal);
        if (controller.signal.aborted) return 'timeout';
        if (tracking === null) {
          return 'unavailable';
        }
        if (tracking.ready) {
          if (tracking.pixels.length === 0) {
            return 'empty';
          }
          trackPurchaseValues(saleId, tracking.pixels, utm);
          await waitForTrackingScripts();
          if (controller.signal.aborted) return 'timeout';
          return 'queued';
        }
      } catch (error) {
        if (controller.signal.aborted) return 'timeout';
        if (error instanceof PurchaseTrackingRequestError && !error.retryable) return 'unavailable';
      }
      await new Promise((resolve) => setTimeout(resolve, Math.min(250 * 2 ** (attempts - 1), 1000)));
    }
    return 'timeout';
  }

  const outcome = await Promise.race([run(), expired]);
  clearTimeout(timer!);
  return outcome;
}
